import { AnnouncementCode, IAnnouncement } from '../../frontend-libs/evlapp-types';
import { createAsyncThunk as asyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  dismissAnnouncement as dismissAnnouncementRequest,
  getAnnouncements,
} from '../../Announcements/services/AnnouncementService';
import type { RootState } from './store';

/**
 * In-app oznámení pro adminy budovy (novinky / onboarding). Načítá se jednou po
 * naběhnutí budovy (PrivateRoute); bannery čte stránka Hlasování, onboarding
 * šipku Sidebar. Dismiss je optimistický — položka zmizí hned, server dostane
 * „beru na vědomí" na pozadí.
 */
interface AnnouncementState {
  items: IAnnouncement[];
  // Budova, ke které items patří — při přepnutí budovy se načítá znovu
  buildingId: string | null;
  isLoaded: boolean;
}

const initialState: AnnouncementState = {
  items: [],
  buildingId: null,
  isLoaded: false,
};

// buildingId slouží jen k evidenci, ke které budově items patří (service čte aktivní budovu ze store)
export const fetchAnnouncements = asyncThunk('announcement/fetch', async (_buildingId: string) => {
  return getAnnouncements();
});

export const dismissAnnouncement = asyncThunk('announcement/dismiss', async (code: AnnouncementCode) => {
  return dismissAnnouncementRequest(code);
});

export const announcementSlice = createSlice({
  name: 'announcement',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(fetchAnnouncements.pending, (state, action) => {
      state.buildingId = action.meta.arg;
      state.isLoaded = false;
      state.items = [];
    });

    builder.addCase(fetchAnnouncements.fulfilled, (state, { payload }) => {
      state.items = payload.items;
      state.isLoaded = true;
    });

    // Starší backend bez endpointu (404) — tiše bez oznámení, appka běží dál.
    builder.addCase(fetchAnnouncements.rejected, (state) => {
      state.items = [];
      state.isLoaded = true;
    });

    builder.addCase(dismissAnnouncement.pending, (state, action) => {
      state.items = state.items.filter((item) => item.code !== action.meta.arg);
    });

    builder.addCase(dismissAnnouncement.fulfilled, (state, { payload }) => {
      state.items = payload.items;
    });
  },
});

export const selectAnnouncements = (state: RootState): AnnouncementState => state.announcement;
export default announcementSlice.reducer;
